import React, { forwardRef, useEffect, useState } from 'react'
import ReactDatePicker, { CalendarContainer } from 'react-datepicker'
import { parse, isValid, format } from 'date-fns'
import "react-datepicker/dist/react-datepicker.css";
import { IconCalendar } from '@tabler/icons-react';
import { CalendarHeader } from './calendar-input-header';
import { maskDateInput } from '@utils/lib/dateMask';
import s from "./calendar-input.module.scss"

const DATE_FORMAT = 'dd.MM.yyyy'

// Кастомный инпут объявлен на уровне модуля: при объявлении внутри рендера
// ReactDatePicker ремаунтил бы DOM-элемент при каждом рендере, теряя фокус.
// Текст редактируется локально: ReactDatePicker на каждый keystroke пересинхронизирует
// value и переформатирует строку (каретка улетает в конец), поэтому дату из ввода
// коммитим явно — по Enter или blur. Календарь открывается только по иконке.
// Свои пропы названы textValue/onCommit, чтобы не пересекаться с пропами,
// которые ReactDatePicker принудительно инжектит в customInput (value, onChange и т.д.).
// placeholder задаём дефолтом здесь: ReactDatePicker инжектит в customInput
// свой placeholder (из placeholderText, часто undefined), который через cloneElement
// перебивает проп элемента — default-параметр отрабатывает и в этом случае.
const DatePickerInput = forwardRef(({ textValue, onCommit, placeholder = 'ДД.ММ.ГГГГ', onClick, ...injected }, ref) => {
	const [text, setText] = useState(textValue ?? '')

	// Внешнее значение (выбор в календаре/проп) подменяет текст только после коммита
	useEffect(() => {
		setText(textValue ?? '')
	}, [textValue])

	const commit = () => {
		const parsed = parse(text, DATE_FORMAT, new Date())

		if (isValid(parsed) && text === format(parsed, DATE_FORMAT)) {
			onCommit(parsed)
		} else {
			// Невалидный ввод — возвращаем отображение последней выбранной даты
			setText(textValue ?? '')
		}
	}

	return (
		<div className={s.inputsWrapper} ref={ref}>
			<input
				value={text}
				placeholder={placeholder}
				inputMode="numeric"
				onChange={(e) => setText(maskDateInput(e.target.value))}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						e.preventDefault()
						commit()
					}
				}}
			/>
			<button type="button" className={s.calendarButton} onClick={onClick} aria-label="Открыть календарь">
				<IconCalendar size={20} color='#919396'/>
			</button>
		</div>
	)
})

export const CalendarInput = ({placeholder, className, label, onChange, value}) => {
	const [date, setDate] = useState(value ? new Date(value) : null)

	const MyContainer = ({ className, children }) => {
    return (
			<CalendarContainer className={className + ' ' + s.wrapper}>
				<div style={{ position: "relative" }}>{children}</div>
			</CalendarContainer>
    );
  };

	const displayValue = date ? format(date, DATE_FORMAT) : ''

	return (
		<div className={`${className} ${s.calendarGroup}`}>
			{label && <div className={s.label}>
				<span>{label}</span>
			</div>}
			 <ReactDatePicker
				 selected={date}
				 onChange={(update) => {
					 setDate(update ? new Date(update) : null)
					 onChange(update)
				 }}
				 customInput={
					 <DatePickerInput
						 placeholder={placeholder}
						 textValue={displayValue}
						 onCommit={(committed) => {
							 setDate(committed)
							 onChange(committed)
						 }}
					 />
				 }
				 calendarClassName={s.wrapper}
				 renderCustomHeader={CalendarHeader}
				 calendarContainer={MyContainer}
				 minDate={new Date(1900, 0, 1)}
				 dateFormat={DATE_FORMAT}
			 />
		</div>
	)
}
